import axios from 'axios';
import { appOperations } from '../../../../App/store';
import ColoredSnackbarConstants from '../../../misc/ColoredSnackbar/ColoredSnackbarConstants';
import { trackEvent, AnalyticsConstants, trackTransaction } from '../../../../utilities/analyticsUtilities';
import types from './types';
import { getCurrencyDisplayName } from '../../../../utilities/stringUtilities'
import { collectionHelper } from '../../../../utilities/basicUtilities';

// DUMB ACTIONS

const setProperty = (key, value) => {
    return {
        type: types.SET_PROPERTY,
        key: key,
        value: value
    }
}

const setCheckingOutId = (id) => {
    return {
        type: types.SET_CHECKING_OUT_ID,
        id: id
    }
}

const setCancelingSubscription = (isCanceling) => {
    return {
        type: types.SET_CANCELING_SUBSCRIPTION,
        isCanceling: isCanceling
    }
}

const setReactivatingSubscription = (isReactivating) => {
    return {
        type: types.SET_REACTIVATING_SUBSCRIPTION,
        isReactivating: isReactivating
    }
}

const setPausingSubscription = (isPausing) => {
    return {
        type: types.SET_PAUSING_SUBSCRIPTION,
        isPausing: isPausing
    }
}

const setUnpausingSubscription = (isUnpausing) => {
    return {
        type: types.SET_UNPAUSING_SUBSCRIPTION,
        isUnpausing: isUnpausing
    }
}

const setUpdatingSubscriptionId = (id) => {
    return {
        type: types.SET_UPDATING_SUBSCRIPTION_ID,
        id: id
    }
}

// SMART OPERATIONS

const initiateCheckout = (optionId, quantity, location) => {
    return (dispatch, getState) => {
        const state = getState();
        const option = collectionHelper(state.app.purchaseOptions).firstOrDefault((po) => po.id === optionId);

        trackEvent('Checkout Started', {
            value: option.amount,
            currency: getCurrencyDisplayName(option.currency),
            contents: [
                {
                    id: optionId,
                    quantity: 1
                }
            ],
            content_type: 'product',
            location: location,
            productId: optionId,
            category: AnalyticsConstants.ECOMMERCE_CATEGORY
        });

        dispatch(setCheckingOutId(optionId));

        axios.post(`/api/plan/GetCheckoutSession`, {
            purchaseOptionId: optionId,
            quantity: quantity,
            clientReferenceId: window.Rewardful?.referral,
            referenceCouponId: window.Rewardful?.coupon?.id
        }).then(response => {
            const script = document.createElement('script');
            script.src = 'https://js.stripe.com/v3/';
            script.async = true;
            script.onload = () => {
                var stripe = window.Stripe(response.data.publishableKey);
                stripe.redirectToCheckout({
                    sessionId: response.data.sessionId
                }).then(function (result) {
                    dispatch(appOperations.openSnack(result.error.message, ColoredSnackbarConstants.SNACKBAR_ERROR))
                });
            };

            document.body.appendChild(script);
        }).catch(error => {
            if ( error.response.data.showForUser) {
                dispatch(appOperations.showInformationDialog(true, '', error.response.data.message));
            } else {
                dispatch(appOperations.failRequest(error, () => initiateCheckout(optionId, quantity, location), 'Failed to initiate checkout.'));
            }
        }).finally(() => {
            dispatch(setCheckingOutId(null));
        });
    }
}

const cancelSubscription = (reason) => {
    return dispatch => {
        dispatch(setCancelingSubscription(true));
        axios.post('/api/plan/CancelSubscription').then(response => {
            dispatch(appOperations.getAccountDetails());

            if (reason.length > 0) {
                trackEvent('Gave Cancellation Reason', {
                    reason: reason,
                    category: AnalyticsConstants.ECOMMERCE_CATEGORY
                });
            }
        }).catch(error => {
            dispatch(appOperations.failRequest(error, () => cancelSubscription(reason), 'Failed to cancel subscription. Please contact us at help@specterr.com for help.'));
        }).finally(() => {
            dispatch(setCancelingSubscription(false));
        });
    }
}

const reactivateSubscription = () => {
    return dispatch => {
        dispatch(setReactivatingSubscription(true));
        axios.post('/api/plan/ReactivateSubscription').then(response => {
            dispatch(appOperations.getAccountDetails());
        }).catch(error => {
            dispatch(appOperations.failRequest(error, () => reactivateSubscription(), 'Failed to reactivate subscription. Please contact us at help@specterr.com for help.'));
        }).finally(() => {
            dispatch(setReactivatingSubscription(false));
        });
    }
}

const pauseSubscription = (months) => {
    return dispatch => {
        dispatch(setPausingSubscription(true));
        axios.post('/api/plan/PauseSubscription/' + months).then(response => {
            dispatch(appOperations.getAccountDetails());
        }).catch(error => {
            dispatch(appOperations.failRequest(error, () => pauseSubscription(months), 'Failed to pause subscription. Please contact us at help@specterr.com for help.'));
        }).finally(() => {
            dispatch(setPausingSubscription(false));
        });
    }
}

const unpauseSubscription = () => {
    return dispatch => {
        dispatch(setUnpausingSubscription(true));
        axios.post('/api/plan/UnpauseSubscription').then(response => {
            dispatch(appOperations.getAccountDetails());
        }).catch(error => {
            dispatch(appOperations.failRequest(error, () => unpauseSubscription(), 'Failed to unpause subscription. Please contact us at help@specterr.com for help.'));
        }).finally(() => {
            dispatch(setUnpausingSubscription(false));
        });
    }
}

const updateSubscription = (optionId, isCancellationRecoveryFlow) => {
    return (dispatch, getState) => {
        dispatch(setUpdatingSubscriptionId(optionId));
        dispatch(setProperty('updatingSubscription', true));

        const updateSubscriptionRequest = {
            purchaseOptionId : optionId,
            isCancellationRecoveryFlow
        };
        
        axios.post('/api/plan/UpdateSubscription', updateSubscriptionRequest).then(response => {
            setTimeout(() => {
                dispatch(appOperations.getAccountDetails());
                dispatch(setProperty('updatingSubscription', false));
            }, 5000);

            dispatch(appOperations.openSnack('Subscription updated successfully!', ColoredSnackbarConstants.SNACKBAR_SUCCESS));

            const state = getState();
            const option = collectionHelper(state.app.purchaseOptions).firstOrDefault((po) => po.id === optionId);
        }).catch(error => {
            dispatch(setProperty('updatingSubscription', false));
            if (error.response.data.showForUser) {
                dispatch(appOperations.showInformationDialog(true, '', error.response.data.message));
            } else {
                dispatch(appOperations.failRequest(error, () => updateSubscription(optionId), 'Failed to update subscription. Please contact us at help@specterr.com for help.'));
            }
        }).finally(() => {
            dispatch(setUpdatingSubscriptionId(null));
        });
    }
}

const navigateToSubscriptionRecoveryPaymentPage  = () => {
    return dispatch => {
        trackEvent('Began Subscription Recovery', {
            category: AnalyticsConstants.ECOMMERCE_CATEGORY,
        });

        axios.get('/api/plan/GetStripePaymentPage').then(response => {
            const customerPortalUrl = response.data;
            window.location = customerPortalUrl;
        }).catch(error => {
            dispatch(appOperations.failRequest(
                error, 
                () => navigateToSubscriptionRecoveryPaymentPage(), 
                'Failed to navigate to the customer portal.'
            ));
        });
    }
}

const trackSuccesfulPayment = (sessionId) => {
    return dispatch => {
        axios.get(`/api/plan/GetSuccesfulPaymentDetails/${sessionId}`).then(response => {
            trackTransaction(response.data);
        }).catch(error => {
            trackEvent('Failed Transaction Tracking', {
                category: AnalyticsConstants.PERFORMANCE_CATEGORY
            });
        });
    }
}

const getTunebatPromotionCode = () => {
    return dispatch => {
        axios.get(`/api/plan/GetTunebatPromotionCode`).then(response => {
            dispatch(setProperty('tunebatPromotionCode', response.data));
        }).catch(error => {
            dispatch(appOperations.failRequest(error, ColoredSnackbarConstants.SNACKBAR_ERROR, "Unable to get promotion code. Try again later or reach us at help@specterr.com."))
        });
    }
}

export default {
    setProperty,
    initiateCheckout,
    cancelSubscription,
    reactivateSubscription,
    pauseSubscription,
    unpauseSubscription,
    updateSubscription,
    trackSuccesfulPayment,
    getTunebatPromotionCode,
    navigateToSubscriptionRecoveryPaymentPage
}